import { useState } from "react";
import { supabase } from "@/lib/supabaseClient";
import { type Lead, STATUS_CFG, fmtDate, fmtDateTime } from "../types";
import { sourceCfg } from "@/pages/admin/leads/components/LeadsDashboard";

const EVENT_TYPES = [
  "Wedding", "XV Años", "Baptism", "Graduation", "Birthday",
  "Baby Shower", "Engagement", "Anniversary", "Corporate Event", "Portrait", "Other",
];

const STATUSES = ["new", "contacted", "booked", "lost"];
const INPUT = "w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-800 placeholder-slate-300 outline-none focus:border-[#C8A96E]/60 focus:bg-white transition-all";

interface Props {
  lead: Lead;
  onLeadUpdate: (updated: Partial<Lead>) => void;
}

export default function OverviewTab({ lead, onLeadUpdate }: Props) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [form, setForm] = useState({
    name:       lead.name,
    email:      lead.email      ?? "",
    phone:      lead.phone      ?? "",
    event_type: lead.event_type ?? "",
    event_date: lead.event_date ?? "",
    message:    lead.message    ?? "",
    status:     lead.status,
  });

  const set = (k: string, v: string) => setForm((p) => ({ ...p, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    const payload = {
      name:       form.name.trim(),
      email:      form.email.trim()      || null,
      phone:      form.phone.trim()      || null,
      event_type: form.event_type        || null,
      event_date: form.event_date        || null,
      message:    form.message.trim()    || null,
      status:     form.status,
      updated_at: new Date().toISOString(),
    };
    await supabase
      .from("tenant_public_leads")
      .update(payload)
      .eq("id", lead.id)
      .eq("tenant_id", lead.tenant_id);
    onLeadUpdate(payload);
    setEditing(false);
    setSaving(false);
  };

  const statusCfg = STATUS_CFG[lead.status] ?? STATUS_CFG["new"];
  const src = sourceCfg(lead.source ?? null);

  if (editing) {
    return (
      <div className="space-y-5">
        <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-slate-700 font-semibold text-sm">Edit Information</h3>
            <button onClick={() => setEditing(false)} className="text-slate-400 hover:text-slate-600 text-xs cursor-pointer">Cancel</button>
          </div>

          <div>
            <label className="text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5 block">Full Name *</label>
            <input value={form.name} onChange={(e) => set("name", e.target.value)} className={INPUT} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5 block">Email</label>
              <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={INPUT} placeholder="email@example.com" />
            </div>
            <div>
              <label className="text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5 block">Phone</label>
              <input value={form.phone} onChange={(e) => set("phone", e.target.value)} className={INPUT} placeholder="+1 555 000 0000" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5 block">Event Type</label>
              <select value={form.event_type} onChange={(e) => set("event_type", e.target.value)} className={`${INPUT} cursor-pointer`}>
                <option value="">— Select —</option>
                {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5 block">Event Date</label>
              <input type="date" value={form.event_date} onChange={(e) => set("event_date", e.target.value)} className={`${INPUT} cursor-pointer`} />
            </div>
          </div>
          <div>
            <label className="text-slate-500 text-xs font-medium uppercase tracking-wider mb-2 block">Status</label>
            <div className="flex gap-2 flex-wrap">
              {STATUSES.map((s) => {
                const cfg = STATUS_CFG[s];
                return (
                  <button key={s} type="button" onClick={() => set("status", s)}
                    className={`flex-1 py-2 rounded-lg text-xs font-medium border transition-all cursor-pointer whitespace-nowrap ${
                      form.status === s ? `${cfg.badge} font-semibold` : "bg-slate-50 text-slate-400 border-slate-200 hover:border-slate-300"
                    }`}>
                    {cfg.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <label className="text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5 block">Message / Notes</label>
            <textarea value={form.message} onChange={(e) => set("message", e.target.value)} rows={3} maxLength={500} className={`${INPUT} resize-none`} placeholder="Lead message..." />
          </div>
          <div className="flex justify-end gap-3 pt-2 border-t border-slate-100">
            <button onClick={() => setEditing(false)} className="px-4 py-2 text-sm text-slate-500 border border-slate-200 rounded-lg hover:bg-slate-50 cursor-pointer whitespace-nowrap">Cancel</button>
            <button onClick={handleSave} disabled={saving || !form.name.trim()}
              className="flex items-center gap-2 px-5 py-2 bg-[#C8A96E] hover:bg-[#b8965a] disabled:opacity-50 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap">
              {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <i className="ri-save-line"></i>}
              Save
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Contact card */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-slate-600 text-xs font-medium uppercase tracking-wider">Contact Info</h3>
          <button onClick={() => setEditing(true)}
            className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-[#9a7840] hover:bg-[#C8A96E]/8 px-2.5 py-1.5 rounded-lg transition-all cursor-pointer whitespace-nowrap">
            <i className="ri-edit-2-line"></i> Edit
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            { label: "Full Name",   value: lead.name,       icon: "ri-user-line" },
            { label: "Email",       value: lead.email,      icon: "ri-mail-line",  href: lead.email ? `mailto:${lead.email}` : undefined },
            { label: "Phone",       value: lead.phone,      icon: "ri-phone-line", href: lead.phone ? `tel:${lead.phone}` : undefined },
            { label: "Event Type",  value: lead.event_type, icon: "ri-calendar-event-line" },
            { label: "Event Date",  value: fmtDate(lead.event_date), icon: "ri-calendar-line" },
            { label: "Source",      value: null,            icon: "ri-global-line" },
          ].map((item) => (
            <div key={item.label}>
              <p className="text-slate-400 text-[10px] uppercase tracking-wider mb-1">{item.label}</p>
              {item.label === "Source" ? (
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${src.cls}`}>
                  <i className={`${src.icon} text-[10px]`}></i>
                  {src.label}
                </span>
              ) : item.href ? (
                <a href={item.href} className="text-slate-700 text-sm hover:text-[#9a7840] transition-colors cursor-pointer flex items-center gap-1.5">
                  <i className={`${item.icon} text-slate-400 text-xs`}></i>
                  {item.value ?? <span className="text-slate-300 italic">Not provided</span>}
                </a>
              ) : (
                <p className="text-slate-700 text-sm flex items-center gap-1.5">
                  <i className={`${item.icon} text-slate-400 text-xs`}></i>
                  {item.value ?? <span className="text-slate-300 italic">Not provided</span>}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Status card */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="text-slate-600 text-xs font-medium uppercase tracking-wider mb-3">Status</h3>
        <div className="flex items-center gap-3 flex-wrap">
          {STATUSES.map((s) => {
            const cfg = STATUS_CFG[s];
            const isActive = lead.status === s;
            return (
              <button key={s}
                onClick={async () => {
                  await supabase.from("tenant_public_leads").update({ status: s, updated_at: new Date().toISOString() }).eq("id", lead.id).eq("tenant_id", lead.tenant_id);
                  onLeadUpdate({ status: s });
                }}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs font-medium border transition-all cursor-pointer whitespace-nowrap ${
                  isActive ? `${cfg.badge} font-semibold` : "bg-slate-50 text-slate-400 border-slate-200 hover:border-slate-300"
                }`}>
                <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`}></span>
                {cfg.label}
                {isActive && <i className="ri-check-line text-[10px]"></i>}
              </button>
            );
          })}
        </div>
      </div>

      {/* Message card */}
      {lead.message && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="text-slate-600 text-xs font-medium uppercase tracking-wider mb-3">Original Message</h3>
          <p className="text-slate-600 text-sm leading-relaxed whitespace-pre-wrap">{lead.message}</p>
        </div>
      )}

      {/* Meta */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="text-slate-600 text-xs font-medium uppercase tracking-wider mb-3">Timeline</h3>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-slate-400 text-xs">Received</span>
            <span className="text-slate-600 text-xs">{fmtDateTime(lead.created_at)}</span>
          </div>
          {lead.updated_at && lead.updated_at !== lead.created_at && (
            <div className="flex items-center justify-between">
              <span className="text-slate-400 text-xs">Last updated</span>
              <span className="text-slate-600 text-xs">{fmtDateTime(lead.updated_at)}</span>
            </div>
          )}
          <div className="flex items-center justify-between">
            <span className="text-slate-400 text-xs">Lead ID</span>
            <span className="text-slate-300 text-[10px] font-mono">{lead.id.slice(0, 8)}…</span>
          </div>
        </div>
      </div>
    </div>
  );
}